import type {
  ClientDataTransfer,
  ClientDataTransferWritePayload,
  DataTransferRun,
} from '../types/data-transfers.types';

export function formatBytesProcessed(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '0 B';
  }
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

export function groupTransfersByClient(
  transfers: ClientDataTransfer[]
): { clientTitle: string; transfers: ClientDataTransfer[] }[] {
  const groups = new Map<string, ClientDataTransfer[]>();
  for (const transfer of transfers) {
    const existing = groups.get(transfer.client_title) ?? [];
    existing.push(transfer);
    groups.set(transfer.client_title, existing);
  }
  return [...groups.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([clientTitle, clientTransfers]) => ({
      clientTitle,
      transfers: [...clientTransfers].sort((left, right) => left.name.localeCompare(right.name)),
    }));
}

export function formatRowCount(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) {
    return '—';
  }
  return value.toLocaleString();
}

export function formatRunDuration(
  startedAt: string | null | undefined,
  finishedAt: string | null | undefined
): string {
  if (!startedAt || !finishedAt) {
    return '—';
  }
  const seconds = Math.max(
    0,
    Math.round((new Date(finishedAt).getTime() - new Date(startedAt).getTime()) / 1000)
  );
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}m ${remainder}s`;
}

const GENERIC_HTTP_MESSAGE = /^Request failed with status code \d+$/i;

function flattenErrorMessages(value: unknown): string[] {
  if (typeof value === 'string' && value.trim()) {
    return [value.trim()];
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return [String(value)];
  }
  if (Array.isArray(value)) {
    return value.flatMap(flattenErrorMessages);
  }
  if (value && typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).flatMap(flattenErrorMessages);
  }
  return [];
}

function formatPayloadError(payload: unknown): string | null {
  if (typeof payload === 'string' && payload.trim()) {
    return payload.trim();
  }
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const record = payload as Record<string, unknown>;
  for (const key of ['detail', 'message', 'error'] as const) {
    const messages = flattenErrorMessages(record[key]);
    if (messages.length) {
      return messages.join('\n');
    }
  }

  const fieldMessages = Object.entries(record).flatMap(([field, value]) => {
    if (field === 'status' || field === 'item') {
      return [];
    }
    return flattenErrorMessages(value).map((message) => (
      field === 'non_field_errors' ? message : `${field}: ${message}`
    ));
  });
  if (fieldMessages.length) {
    return fieldMessages.join('\n');
  }

  return null;
}

export function formatDataTransferApiError(
  error: unknown,
  fallback = 'Request failed.'
): string {
  if (typeof error === 'string' && error.trim()) {
    return error.trim();
  }
  if (!error || typeof error !== 'object') {
    return fallback;
  }

  const record = error as Record<string, unknown>;
  const payload = record.responseData ?? record.data;
  const fromPayload = formatPayloadError(payload);
  if (fromPayload) {
    return fromPayload;
  }

  if (typeof record.message === 'string' && record.message.trim()) {
    const message = record.message.trim();
    if (!GENERIC_HTTP_MESSAGE.test(message)) {
      return message;
    }
  }

  const status = record.httpStatus ?? record.status;
  if (typeof status === 'number') {
    return `${fallback} (HTTP ${status})`;
  }

  return fallback;
}

export type {
  ClientDataTransfer,
  ClientDataTransferWritePayload,
  DataTransferRun,
};
